import React, {useRef, useState, useCallback} from 'react';
import {Box, Grid, Typography, IconButton, Divider, Alert} from '@mui/material';
import {useReactToPrint} from 'react-to-print';
import PrintIcon from '@mui/icons-material/Print';
import moment from 'moment';
import {isNumber, uniqBy} from 'lodash';
import parseHTML from 'html-react-parser';
import {useLazyQuery} from '@apollo/client';
import {Button} from '../../components/Button/Button';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {PrintableRiskSelector} from './RiskSelector';
import {RiskType} from '../../schema-types/globalTypes';
import {
  GetRisk,
  GetRisk_reviewDetails_contacts,
  GetRisk_reviewDetails_riskProfiles_previousSections,
  GetRiskVariables,
} from '../../schema-types/GetRisk';
import {riskTypeToDisplays} from './riskText';
import {sortAccountContacts} from '../../components/AccountOverview/sortAccountContacts';
import {riskSegmentsOrderedByBusinessLogic} from './RiskFormHelpers';
import {GET_RISK} from './riskQueries';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import Loading from '../../components/Loading/Loading';
import {Prompt} from '../../components/Prompt/Prompt';
import {useRTQ} from '../../hooks/useRTQ';
import {PrintStyles} from './PrintStyles';
import validateUUID from '../../utils/validateUUID';

/**
 * Helper function to get previous risk sections for a specific risk type.
 *
 * Each historic RiskProfile (one per prior review) carries its own copy of the
 * consolidated sections, so the same note recurs once per historic profile —
 * on long-history accounts that meant a note could render dozens of times per
 * section in the printed PDF (ON-9326). We drop empty notes and de-duplicate by
 * note content, mirroring the on-screen editor (RiskNotesEditor.getPreviousSections
 * + uniqBy), which already did this; the printable copy previously did not.
 */
export const getPreviousSections = (
  risk: GetRisk,
  riskType: RiskType
): GetRisk_reviewDetails_riskProfiles_previousSections[] => {
  let previousSections: GetRisk_reviewDetails_riskProfiles_previousSections[] = [];
  risk.reviewDetails?.riskProfiles.forEach((rp) => {
    previousSections = previousSections.concat(rp.previousSections);
  });
  return uniqBy(
    previousSections.filter(
      (ps) => ps.riskType === riskType && (ps.notes?.length ?? 0) > 0
    ),
    'notes'
  );
};

/**
 * Component for an individual risk segment
 */
const RiskSegment: React.FC<{
  riskType: RiskType;
  risk: GetRisk;
  contacts: GetRisk_reviewDetails_contacts[];
  print: boolean;
}> = ({riskType, risk, contacts, print}) => {
  const riskProfiles = risk?.reviewDetails?.riskProfiles;
  const sections = riskProfiles
    ?.map((profile) => profile.sections.find((s) => s.riskType === riskType))
    .filter(Boolean);

  // if (!sections?.length || sections.every((s) => s?.noChange)) {
  if (!sections) {
    return null;
  }

  const display = riskTypeToDisplays[riskType];
  const previousSections = getPreviousSections(risk, riskType);
  const isMultiscoreEnabled = true;
  return (
    <>
      <Box className="no-break" mb={4} p={2} sx={{borderBottom: '1px solid #ccc'}}>
        <Typography variant="h5" mb={1}>
          {display.title}
        </Typography>

        {isMultiscoreEnabled ? (
          // If multiscore is enabled, we need to show score for each contact
          sections?.map((section, index) => {
            const contactProfile = riskProfiles?.[index];
            const contact = contacts?.find((c) => c.id === contactProfile?.contact?.id);

            if (!section || !contact) return null;

            return (
              <Box key={contact.id} mb={2}>
                <Typography variant="h6">{contact.simplifiedName}</Typography>
                {isNumber(section.score) && (
                  <Box mb={1}>
                    <Typography variant="subtitle1">Impact on Risk Rating:</Typography>
                    <PrintableRiskSelector
                      currentScore={section.score}
                      readOnly
                      size="small"
                      scoreTexts={display['Impact on Risk Rating']}
                    />
                  </Box>
                )}
              </Box>
            );
          })
        ) : (
          // If multiscore is not enabled, we only need to show one score
          <>
            {isNumber(sections[0]?.score) && (
              <Box mb={2}>
                <Typography variant="subtitle1">Impact on Risk Rating:</Typography>
                <PrintableRiskSelector
                  currentScore={sections[0]?.score}
                  readOnly
                  size="small"
                  scoreTexts={display['Impact on Risk Rating']}
                />
              </Box>
            )}
          </>
        )}

        {/* Notes - show only the first section's notes as they should be the same across contacts */}
        {sections[0]?.notes && (
          <Box mb={2}>
            <Typography variant="subtitle1">Notes:</Typography>
            <Typography sx={{whiteSpace: 'pre-wrap'}}>
              {sections[0].notes ? parseHTML(sections[0].notes) : sections[0].notes}
            </Typography>
          </Box>
        )}

        {/* Previous risk discussions */}
        {previousSections.length > 0 && (
          <Box mb={2} mt={3}>
            <Typography variant="h6">Previous {display.title} discussions:</Typography>
            <Box
              sx={
                print
                  ? {}
                  : {
                      mt: 1,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      border: `1px solid ${themeColors.greyLight}`,
                      borderRadius: 1,
                      p: 2,
                    }
              }
            >
              {previousSections.map((ps, index) => (
                <Box key={ps.createdAt || ps.notes} mb={2}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {moment(ps.createdAt).format('MMMM DD, YYYY')}
                  </Typography>
                  {ps.notes && (
                    <Typography sx={{whiteSpace: 'pre-wrap', ml: 2}}>
                      {parseHTML(ps.notes)}
                    </Typography>
                  )}
                  {index < previousSections.length - 1 && <Divider sx={{my: 2}} />}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* <Box mt={2}>
        <Typography variant="subtitle1">Considerations:</Typography>
        <ul>
          {display.Prompts.map((prompt, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{prompt}</li>
          ))}
        </ul>
      </Box> */}
      </Box>
    </>
  );
};

/**
 * Component for displaying risk discussion sections
 */
const RiskDiscussionSection: React.FC<{
  risk: GetRisk;
  contacts: GetRisk_reviewDetails_contacts[];
  accountName?: string;
  print: boolean;
}> = ({risk, contacts, accountName, print}) => {
  return (
    <Box mb={4}>
      <h2>Risk Discussion {accountName ? `for ${accountName}` : ''}</h2>

      {riskSegmentsOrderedByBusinessLogic.map((riskType) => (
        <RiskSegment
          key={riskType}
          print={print}
          riskType={riskType}
          risk={risk}
          contacts={contacts}
        />
      ))}
    </Box>
  );
};

export const PrintableRiskFormInner = React.forwardRef<
  HTMLDivElement,
  {
    risk: GetRisk;
    accountId: string;
    accountName: string;
  }
>((props, ref) => {
  const {atrName} = useRTQ();
  const {risk, accountId, accountName} = props;
  const contacts = sortAccountContacts(accountId, risk?.reviewDetails?.contacts || []);
  const riskProfiles = risk?.reviewDetails?.riskProfiles;

  return (
    <div ref={ref} style={{margin: '1rem'}}>
      {/* Header */}
      <PrintStyles />
      <Box mb={4}>
        <img
          style={{
            width: '100px',
            height: '25px',
            objectFit: 'contain',
            filter: 'grayscale(100%) contrast(200%) invert(100%)',
          }}
          alt="company logo"
          src="/logo.svg"
        />
        <h3 style={{textAlign: 'center', backgroundColor: 'gainsboro'}}>
          {' '}
          Risk Assessment for {accountName}
        </h3>
      </Box>

      {/* Risk Profiles Summary */}
      <Box mb={4}>
        <h2>Risk Profile Summary</h2>
        <Grid container spacing={4}>
          {contacts?.map((contact) => {
            const riskProfile = riskProfiles?.find((p) => p.contact.id === contact.id);
            const initialRiskProfile = riskProfile?.initialRiskProfile || null;
            const initialRiskQuestionnaire =
              riskProfile?.initialRiskToleranceQuestionnaire || null;

            return (
              <Grid item xs={12} key={contact.id} mb={4}>
                <Panel
                  color={themeColors.greyLight}
                  title={`${contact.simplifiedName}'s Risk Profile`}
                >
                  <Box p={2}>
                    {initialRiskQuestionnaire?.assessedAt ? (
                      <>
                        {initialRiskQuestionnaire.isExpired && (
                          <Box mb={1}>
                            <Alert severity="warning">Risk profile has expired</Alert>
                          </Box>
                        )}
                        <Box mb={1}>
                          <Typography variant="h6">Assessment Date:</Typography>
                          <Typography>
                            {moment(initialRiskQuestionnaire?.assessedAt).format(
                              'DD/MM/YYYY'
                            )}{' '}
                            -{' '}
                            {moment(initialRiskQuestionnaire?.expiryDate).format(
                              'DD/MM/YYYY'
                            )}
                          </Typography>
                        </Box>
                      </>
                    ) : null}

                    {isNumber(initialRiskQuestionnaire?.score) ? (
                      <Box mb={1}>
                        <Typography variant="h6">
                          Risk Tolerance Questionnaire:
                        </Typography>
                        <Typography>
                          Result of the most recent {atrName} to the creation of this
                          review.
                        </Typography>
                        <PrintableRiskSelector
                          currentScore={initialRiskQuestionnaire.score}
                          readOnly
                          size="small"
                        />
                      </Box>
                    ) : (
                      !initialRiskQuestionnaire?.isExpired && (
                        <Box mb={1}>
                          <Typography variant="h6">
                            Risk Tolerance Questionnaire:
                          </Typography>
                          <Typography mb={0.5}>
                            Result of the most recent {atrName} to the creation of this
                            review.
                          </Typography>
                          <Typography color="text.secondary">
                            No initial Risk Tolerance Questionnaire data available
                          </Typography>
                        </Box>
                      )
                    )}
                    {(riskProfile?.riskToleranceQuestionnaireDeclineReason?.length || 0) >
                      0 && (
                      <Box mb={1}>
                        <Typography variant="h6">
                          Risk Tolerance Questionnaire Decline Reason:
                        </Typography>
                        <Typography>
                          {riskProfile?.riskToleranceQuestionnaireDeclineReason
                            ? parseHTML(
                                riskProfile.riskToleranceQuestionnaireDeclineReason
                              )
                            : riskProfile?.riskToleranceQuestionnaireDeclineReason}
                        </Typography>
                      </Box>
                    )}

                    {isNumber(initialRiskProfile?.score) ? (
                      <Box mb={1}>
                        <Typography variant="h6">Risk Profile:</Typography>
                        <Typography>Risk Profile set on previous review.</Typography>
                        <PrintableRiskSelector
                          currentScore={initialRiskProfile.score}
                          readOnly
                          size="small"
                        />
                      </Box>
                    ) : (
                      <Box mb={1}>
                        <Typography variant="h6">Risk Profile:</Typography>
                        <Typography>Risk Profile set on previous review.</Typography>
                        <Typography color="text.secondary">
                          No Risk Profile score available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Panel>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Risk Discussion Sections */}
      <RiskDiscussionSection
        print
        accountName={accountName}
        risk={risk}
        contacts={contacts}
      />

      {/* No Change Reason Notes */}
      {riskProfiles?.[0]?.noChangeReasonNotes && (
        <Box mb={4}>
          <h2>No Changes Reason</h2>
          <Typography sx={{whiteSpace: 'pre-wrap'}}>
            {riskProfiles[0].noChangeReasonNotes
              ? parseHTML(riskProfiles[0].noChangeReasonNotes)
              : riskProfiles[0].noChangeReasonNotes}
          </Typography>
        </Box>
      )}
      {/* Liquidity Needs */}
      <Box mb={4} className="no-break">
        <h2>Liquidity Needs</h2>
        {contacts?.map((contact) => {
          return (
            <Box key={contact.id} mb={2} p={2} sx={{borderBottom: '1px solid #ccc'}}>
              <Typography variant="h6">{contact.simplifiedName}</Typography>

              <Box mb={2}>
                <Typography variant="subtitle1">GIA Liquidity Needs:</Typography>
                <Typography>{contact.giaHasLiquidityNeeds ? 'Yes' : 'No'}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle1">Pension Liquidity Needs:</Typography>
                <Typography>{contact.pensionHasLiquidityNeeds ? 'Yes' : 'No'}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Central Proposition & DFM Agreement */}
      {/* <Box mb={4}>
        <h2>Investment Proposition</h2>
        <Box mb={2}>
          <Typography variant="subtitle1">
            Central Investment Proposition Explained:
          </Typography>
          <Typography>
            {riskProfiles?.[0]?.centralPropositionAgreed ? 'Yes' : 'No'}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="subtitle1">
            Discretionary Fund Managers Agreement:
          </Typography>
          <Typography>{riskProfiles?.[0]?.dfmAgreed ? 'Yes' : 'No'}</Typography>
        </Box>
      </Box> */}
      <div className="page-break" />

      <Grid item xs={12} mb={4}>
        <Panel color={themeColors.greyLight} title="Risk Outcome ">
          {contacts?.map((contact) => {
            const riskProfile = riskProfiles?.find((p) => p.contact.id === contact.id);
            return (
              <Box key={contact.id} mb={2} p={2} sx={{borderBottom: '1px solid #ccc'}}>
                <Typography variant="h6">{contact.simplifiedName}</Typography>
                {riskProfile?.outcomeRiskScore ? (
                  <Box mb={1}>
                    <Typography variant="h6">Risk Outcome:</Typography>
                    <PrintableRiskSelector
                      currentScore={riskProfile.outcomeRiskScore}
                      readOnly
                      size="small"
                    />
                  </Box>
                ) : (
                  <Box mb={1}>
                    <Typography variant="h6">Risk Outcome:</Typography>
                    <Typography color="text.secondary">
                      No Risk Outcome score available
                    </Typography>
                  </Box>
                )}
                {riskProfile?.outcomeRiskNotes && (
                  <Box mb={1}>
                    <Typography variant="subtitle1">Risk Outcome Notes:</Typography>
                    <Typography sx={{whiteSpace: 'pre-wrap'}}>
                      {riskProfile.outcomeRiskNotes
                        ? parseHTML(riskProfile.outcomeRiskNotes)
                        : riskProfile.outcomeRiskNotes}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Panel>
      </Grid>
    </div>
  );
});

/**
 * Main component that handles data fetching and printing in one sequence
 */
export const PrintableRiskForm: React.FC = () => {
  const [printData, setPrintData] = useState<GetRisk | null>(null);
  const {accountId, account} = useAccountContext();
  const {reviewId} = useReviewContext();
  const componentRef = useRef<HTMLDivElement | null>(null);

  const [fetchRiskData, {loading, error}] = useLazyQuery<GetRisk, GetRiskVariables>(
    GET_RISK,
    {
      fetchPolicy: 'network-only', // Always fetch fresh data from network
      onCompleted: (data) => {
        // Set the data and it will trigger the print effect
        setPrintData(data);
      },
      // eslint-disable-next-line @typescript-eslint/no-shadow
      onError: (error) => {
        console.error('Error fetching risk data for printing:', error);
      },
    }
  );

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Risk Assessment - ${
      printData?.reviewDetails?.contacts?.[0]?.simplifiedName || account?.name || 'Client'
    }`,
    onAfterPrint: () => {
      // Clear the print data after printing is complete
      setPrintData(null);
    },
  });

  // Handle the print button click
  const onPrintClick = useCallback(() => {
    // Clear any existing print data first
    setPrintData(null);

    // Fetch fresh data
    fetchRiskData({
      variables: {reviewId: validateUUID(reviewId)!},
    });
  }, [fetchRiskData, reviewId]);

  // Effect to trigger print once data is loaded and component is rendered
  React.useEffect(() => {
    if (printData?.reviewDetails && componentRef.current) {
      // Small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        handlePrint();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [printData, handlePrint]);

  // Show loading state while fetching data
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <Loading entityName="risk data for printing" />
      </Box>
    );
  }

  // Show error state if fetch failed
  if (error) {
    return (
      <Box>
        <Prompt
          variant="error"
          title="Error loading risk data for printing"
          content={error.message}
        />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button
            startIcon={
              <IconButton
                aria-label="print"
                color="secondary"
                size="large"
                sx={{margin: 0}}
              >
                <PrintIcon />
              </IconButton>
            }
            eventName="Print risk assessment"
            onClick={onPrintClick}
            variant="text"
          >
            Print Risk Assessment
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hidden component that gets rendered with fresh data for printing */}
      {printData?.reviewDetails && (
        <div style={{display: 'none'}}>
          <PrintableRiskFormInner
            accountName={account?.name || 'Client'}
            ref={componentRef}
            risk={printData}
            accountId={accountId || ''}
          />
        </div>
      )}

      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <Button
          startIcon={
            <IconButton
              aria-label="print"
              color="secondary"
              size="large"
              sx={{margin: 0}}
            >
              <PrintIcon />
            </IconButton>
          }
          eventName="Print risk assessment"
          onClick={onPrintClick}
          variant="text"
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Print Risk Assessment'}
        </Button>
      </Box>
    </Box>
  );
};

export default PrintableRiskForm;
